import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../utils/auth";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
import {
  fetchPrintDashboard,
  completePrintBatch,
  removePrintQueueItem,
  refreshPrintBatch,
  startPrintSession,
  closePrintSession,
  fetchPrintedItems,
  reprintPrintItem,
  resetBatchCounter,
  fetchAllPendingLabels,
  fetchBatchView,
  swapPrintItems,
} from "../../api/PrintQueueAPIs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronLeft,
  ChevronRight,
  Printer,
  RefreshCw,
  CheckCircle2,
  Trash2,
  Plus,
  LayoutGrid,
  Package,
  Clock,
  Layers,
  Tag,
  RotateCcw,
  Loader2,
  ArrowLeftRight,
  X,
  Hash,
} from "lucide-react";
import qz from "qz-tray";
import { printOrderPieceLabels } from "../../utils/qzTray";

const MACHINE_COLUMNS = 6;
const MACHINE_ROWS = 2;

const StatCard = ({ label, value, icon: Icon, accent }) => (
  <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200/80 p-4 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm text-slate-500 mb-1">{label}</p>
        <p className={`text-3xl font-bold ${accent}`}>{value}</p>
      </div>
      <div className="p-2.5 rounded-xl bg-slate-100 text-slate-600">
        <Icon size={22} />
      </div>
    </div>
  </div>
);

const PrintItemCard = ({
  item,
  onRemove,
  isLoading,
  canRemove,
  swapSourceId,
  onSwapClick,
}) => {
  if (!item) {
    return (
      <div className="aspect-[3/4] rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/80 flex flex-col items-center justify-center text-slate-400 animate-pulse">
        <LayoutGrid size={36} className="mb-2 opacity-40" />
        <span className="text-base font-medium">فارغ</span>
      </div>
    );
  }

  const imageSrc = item.designImageUrl
    ? `${API_BASE_URL_V2}${item.designImageUrl}`
    : null;

  const isSwapSource = swapSourceId === item.printQueueItemId;
  const isSwapTargetMode = swapSourceId != null && !isSwapSource;

  return (
    <div
      className={`group relative aspect-[1/1.6] rounded-xl border bg-white shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 overflow-hidden flex flex-col ${
        isSwapSource
          ? "border-amber-400 ring-2 ring-amber-300"
          : isSwapTargetMode
            ? "border-indigo-300 ring-1 ring-indigo-200 cursor-pointer"
            : "border-slate-200"
      }`}
      onClick={() => {
        if (isSwapTargetMode) onSwapClick(item);
      }}
    >
      <div
        className="absolute top-2 right-2 z-10 bg-slate-900/85 text-white text-lg font-bold px-2.5 py-1 rounded-lg"
        style={{ fontFamily: "Arial, sans-serif" }}
      >
        #{item.machinePosition}
      </div>
      <div
        className="absolute top-2 left-2 z-10 bg-indigo-600 text-white text-sm font-bold px-2 py-1 rounded"
        style={{ fontFamily: "Arial, sans-serif" }}
      >
        Q{item.queuePosition}
      </div>

      <button
        type="button"
        disabled={isLoading}
        onClick={(e) => {
          e.stopPropagation();
          onSwapClick(item);
        }}
        className={`absolute top-10 left-2 z-10 p-1.5 rounded-lg shadow transition disabled:opacity-50 ${
          isSwapSource
            ? "bg-amber-500 text-white"
            : "bg-white/95 text-indigo-700 hover:bg-indigo-600 hover:text-white opacity-90 group-hover:opacity-100"
        }`}
        title={isSwapSource ? "إلغاء التبديل" : swapSourceId ? "تبديل مع هذا" : "تبديل المكان"}
      >
        <ArrowLeftRight size={14} />
      </button>

      <div className="h-[82%] bg-slate-100 relative flex items-center justify-center overflow-hidden">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={item.designCode || "design"}
            className="w-full h-full object-contain scale-125"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm px-2 text-center font-medium">
            {item.isMissingDesign ? "صورة غير متوفرة" : "لا توجد صورة"}
          </div>
        )}
      </div>

      <div
        className="shrink-0 p-2 text-left border-t border-slate-100 bg-white space-y-0"
        dir="ltr"
        style={{ fontFamily: "Arial, sans-serif" }}
      >
        <p className="m-0 flex gap-1 text-sm font-semibold text-slate-800 truncate" style={{ fontFamily: "Arial, sans-serif" }}>
          <span>Order:</span>
          <span className="truncate">{item.itemLabel}</span>
        </p>

        <p className="m-0 flex gap-1 text-sm font-semibold text-indigo-600 truncate" style={{ fontFamily: "Arial, sans-serif" }}>
          <span>Code:</span>
          <span className="truncate">{item.designCode || "—"}</span>
        </p>

        <p className="m-0 flex gap-1 text-[11px] font-semibold text-slate-600 truncate" style={{ fontFamily: "Arial, sans-serif" }}>
          <span>Type:</span>
          <span className="truncate">{item.typeName || "نوع غير معروف"}</span>
        </p>

        <p className="m-0 flex gap-1 text-sm font-semibold text-indigo-600" style={{ fontFamily: "Arial, sans-serif" }}>
          <span>Piece:</span>
          <span>
            {item.itemIndexInOrder} / {item.totalItemsInOrder}
          </span>
        </p>
      </div>

      {canRemove && (
        <button
          type="button"
          disabled={isLoading}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(item.printQueueItemId);
          }}
          className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg shadow disabled:opacity-50"
          title="إزالة من الدفعة"
        >
          <Trash2 size={18} />
        </button>
      )}
    </div>
  );
};

const PreviewItem = ({ item, index }) => (
  <div className="flex items-center gap-3 p-2 rounded-lg bg-white/60 border border-slate-100">
    <span className="text-xs font-bold text-slate-400 w-6">{index + 1}</span>
    <div className="w-10 h-10 rounded-lg bg-slate-100 overflow-hidden shrink-0">
      {item.designImageUrl ? (
        <img
          src={`${API_BASE_URL_V2}${item.designImageUrl}`}
          alt=""
          className="w-full h-full object-cover"
        />
      ) : null}
    </div>
    <div className="flex-1 min-w-0 text-right">
      <p className="text-sm font-semibold text-slate-700 truncate">{item.itemLabel}</p>
      <p className="text-xs text-slate-500 truncate">{item.designCode}</p>
    </div>
  </div>
);

const ProductionDashboardPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [batchView, setBatchView] = useState(null);
  const [batchOffset, setBatchOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isPrintingAll, setIsPrintingAll] = useState(false);
  const [isPrintingOrderPieces, setIsPrintingOrderPieces] = useState(false);
  const [printedItems, setPrintedItems] = useState([]);
  const [printedPage, setPrintedPage] = useState(0);
  const [printedHasMore, setPrintedHasMore] = useState(false);
  const [printedTotal, setPrintedTotal] = useState(0);
  const [isLoadingPrinted, setIsLoadingPrinted] = useState(false);
  const [isLoadingMorePrinted, setIsLoadingMorePrinted] = useState(false);
  const [reprintingId, setReprintingId] = useState(null);
  const [swapSourceId, setSwapSourceId] = useState(null);

  const loadPrintedItems = useCallback(async (pageNum = 0, append = false) => {
    if (append) setIsLoadingMorePrinted(true);
    else setIsLoadingPrinted(true);
    try {
      const data = await fetchPrintedItems(pageNum, 12);
      setPrintedItems((prev) => (append ? [...prev, ...(data.items || [])] : data.items || []));
      setPrintedPage(pageNum);
      setPrintedHasMore(!!data.hasMore);
      setPrintedTotal(data.totalCount || 0);
    } catch (err) {
      toast.error(err.message || "فشل تحميل القطع المطبوعة");
    } finally {
      setIsLoadingPrinted(false);
      setIsLoadingMorePrinted(false);
    }
  }, []);

  const loadBatchView = useCallback(async (offset = 0) => {
    try {
      const view = await fetchBatchView(offset);
      setBatchView(view);
      setBatchOffset(view.batchOffset ?? offset);
      return view;
    } catch (err) {
      toast.error(err.message || "فشل تحميل عرض الدفعة");
      return null;
    }
  }, []);

  const loadDashboard = useCallback(async (offset = 0) => {
    try {
      const data = await fetchPrintDashboard();
      setDashboard(data);
      if (data?.session) {
        await loadBatchView(offset);
      } else {
        setBatchView(null);
        setBatchOffset(0);
      }
    } catch (err) {
      toast.error(err.message || "فشل تحميل لوحة الإنتاج");
    } finally {
      setIsLoading(false);
    }
  }, [loadBatchView]);

  useEffect(() => {
    if (authState.role !== "Admin" && authState.role !== "Moderator") {
      navigate("/login");
      return;
    }
    loadDashboard(0);
    loadPrintedItems(0);
  }, [authState, navigate, loadDashboard, loadPrintedItems]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setSwapSourceId(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const runAction = async (action, successMessage) => {
    setActionLoading(true);
    try {
      const data = await action();
      setDashboard(data);
      await loadBatchView(batchOffset);
      if (successMessage) toast.success(successMessage);
      await loadPrintedItems(0);
    } catch (err) {
      toast.error(err.message || "حدث خطأ");
    } finally {
      setActionLoading(false);
    }
  };

  const handleNavigateBatch = async (nextOffset) => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      await loadBatchView(nextOffset);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSwapClick = async (item) => {
    if (!item?.printQueueItemId || actionLoading) return;

    if (!swapSourceId) {
      setSwapSourceId(item.printQueueItemId);
      toast.info("اختر الجراب الثاني للتبديل");
      return;
    }

    if (swapSourceId === item.printQueueItemId) {
      setSwapSourceId(null);
      return;
    }

    setActionLoading(true);
    try {
      const data = await swapPrintItems(swapSourceId, item.printQueueItemId);
      setDashboard(data);
      setSwapSourceId(null);
      await loadBatchView(batchOffset);
      toast.success("تم تبديل المكانين");
    } catch (err) {
      toast.error(err.message || "فشل التبديل");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteBatch = () => {
    if (!batchView?.isCurrentBatch) {
      toast.warning("إكمال الدفعة متاح للدفعة الحالية فقط — ارجع للدفعة الأولى");
      return;
    }
    if (!window.confirm("تأكيد إكمال الدفعة الحالية؟ سيتم وضع علامة مطبوع على جميع العناصر.")) {
      return;
    }
    runAction(completePrintBatch, "تم إكمال الدفعة بنجاح");
  };

  const handleRemoveItem = (itemId) => {
    if (!batchView?.isCurrentBatch) {
      toast.warning("الإزالة متاحة من الدفعة الحالية فقط");
      return;
    }
    if (!window.confirm("إزالة هذا العنصر من الدفعة؟ سيتم سحب العنصر التالي تلقائياً.")) {
      return;
    }
    runAction(() => removePrintQueueItem(itemId), "تمت الإزالة وتحديث الدفعة");
  };

  const handleStartSession = () => {
    if (!window.confirm("بدء جلسة طباعة جديدة؟ سيتم إغلاق الجلسة الحالية إن وجدت.")) {
      return;
    }
    setBatchOffset(0);
    setSwapSourceId(null);
    runAction(startPrintSession, "تم بدء جلسة جديدة");
  };

  const handleCloseSession = () => {
    if (!window.confirm("إغلاق جلسة الطباعة الحالية؟")) {
      return;
    }
    setSwapSourceId(null);
    runAction(closePrintSession, "تم إغلاق الجلسة");
  };

  const handleResetBatchCounter = () => {
    if (
      !window.confirm(
        "تصفير عداد الدفعات؟\n\nرقم الدفعة الحالية سيرجع لصفر، وتقدر تعدّ دفعات اليوم من جديد."
      )
    ) {
      return;
    }
    runAction(resetBatchCounter, "تم تصفير عداد الدفعات");
  };

  const handleReprintItem = async (itemId) => {
    if (
      !window.confirm(
        "إعادة طباعة هذه القطعة؟\n\nهتخش في الدفعة الحالية، وآخر جراب في الماكينة هيرجع للطابور للدفعة التالية."
      )
    ) {
      return;
    }

    setReprintingId(itemId);
    try {
      const data = await reprintPrintItem(itemId);
      setDashboard(data);
      toast.success("تم إدخال القطعة للدفعة الحالية");
      await loadBatchView(0);
      await loadPrintedItems(0);
    } catch (err) {
      toast.error(err.message || "فشل إعادة الطباعة");
    } finally {
      setReprintingId(null);
    }
  };

  const printSlotsAsLabels = async (slots, batchNum) => {
    await qz.websocket.connect();
    const config = qz.configs.create("Xprinter XP-420B");
    const data = [];

    slots.forEach((item, index) => {
      if (!item) return;
      const slotNum = item.machinePosition || index + 1;
      const typeName = String(item.typeName || "");
      const typeMax = 22;
      let typeLines;
      if (typeName.length <= typeMax) {
        typeLines = [`Type: ${typeName}`];
      } else {
        const spaceIdx = typeName.lastIndexOf(" ", typeMax);
        const cut = spaceIdx > 8 ? spaceIdx : typeMax;
        typeLines = [
          `${typeName.slice(0, cut).trim()}`,
          typeName.slice(cut).trim().slice(0, typeMax),
        ];
      }

      let y = 12;
      const lineH = 32;
      const labelLines = [
        `Batch: ${batchNum}`,
        `Order: ${item.orderId}`,
        ...typeLines,
        `Design: ${item.designCode || ""}`,
        `Item: ${item.itemIndexInOrder}/${item.totalItemsInOrder}`,
        `Slot: ${slotNum}/12`,
      ];
      const textCmds = labelLines
        .map((line) => {
          const safe = String(line).replace(/"/g, "'");
          const cmd = `TEXT 5,${y},"3",0,1,1,"${safe}"`;
          y += lineH;
          return cmd;
        })
        .join("\n");

      const tspl = `
SIZE 55 mm,40 mm
GAP 2 mm,0 mm
CLS
${textCmds}
PRINT 1
`;
      data.push({ type: "raw", data: tspl });
    });

    if (data.length === 0) {
      toast.warning("لا توجد عناصر في هذه الدفعة");
      return 0;
    }

    await qz.print(config, data);
    return data.length;
  };

  const handlePrintBatch = async () => {
    setIsPrinting(true);
    try {
      const slotsToPrint = batchView?.slots || [];
      const batchNum = batchView?.displayBatchNumber ?? 0;
      const count = await printSlotsAsLabels(slotsToPrint, batchNum);
      if (count > 0) {
        toast.success(`تم طباعة ${count} ملصق للدفعة #${batchNum}`);
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "فشل الطباعة");
    } finally {
      setIsPrinting(false);
    }
  };

  const handlePrintAllLabels = async () => {
    setIsPrintingAll(true);
    try {
      const result = await fetchAllPendingLabels();
      const items = result.items || [];

      if (items.length === 0) {
        toast.warning("لا توجد قطع للطباعة");
        return;
      }

      const confirmed = window.confirm(
        `طباعة كل الملصقات؟\n\n${result.totalLabels} ملصق على ${result.totalBatches} دفعة\n(كل ملصق فيه رقم الدفعة)`
      );
      if (!confirmed) return;

      await qz.websocket.connect();
      const config = qz.configs.create("Xprinter XP-420B");

      const data = items.map((item) => {
        const tspl = `
SIZE 55 mm,40 mm
GAP 2 mm,0 mm
CLS
TEXT 40,20,"3",0,1,1,"Batch: ${item.batchNumber}"
TEXT 40,55,"3",0,1,1,"Order: ${item.orderId}"
TEXT 40,90,"3",0,1,1,"Type: ${item.typeName || ""}"
TEXT 40,125,"3",0,1,1,"Design: ${item.designCode || ""}"
TEXT 40,160,"3",0,1,1,"Item: ${item.itemIndexInOrder}/${item.totalItemsInOrder}"
TEXT 40,195,"3",0,1,1,"Slot: ${item.machinePosition}/12"
PRINT 1
`;
        return { type: "raw", data: tspl };
      });

      await qz.print(config, data);
      toast.success(`تم طباعة ${data.length} ملصق (${result.totalBatches} دفعة)`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "فشل طباعة كل الملصقات");
    } finally {
      setIsPrintingAll(false);
    }
  };

  const handlePrintOrderPieceLabels = async () => {
    setIsPrintingOrderPieces(true);
    try {
      const result = await fetchAllPendingLabels();
      const items = result.items || [];

      const byOrder = new Map();
      for (const item of items) {
        if (item?.orderId == null) continue;
        if (!byOrder.has(item.orderId)) {
          byOrder.set(item.orderId, item.totalItemsInOrder || 0);
        }
      }

      // Fallback: unique orders from the currently visible batch
      if (byOrder.size === 0) {
        for (const item of batchView?.slots || []) {
          if (!item?.orderId) continue;
          if (!byOrder.has(item.orderId)) {
            byOrder.set(item.orderId, item.totalItemsInOrder || 0);
          }
        }
      }

      if (byOrder.size === 0) {
        toast.warning("لا توجد طلبات للطباعة");
        return;
      }

      const labels = Array.from(byOrder.entries()).map(([orderNumber, pieces]) => ({
        orderNumber,
        pieces,
      }));

      if (
        !window.confirm(
          `طباعة ملصقات رقم الأوردر + عدد القطع؟\n\n${labels.length} ملصق على ورق 50×25`
        )
      ) {
        return;
      }

      const count = await printOrderPieceLabels(labels);
      toast.success(`تم طباعة ${count} ملصق`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "فشل طباعة ملصقات الأوردرات");
    } finally {
      setIsPrintingOrderPieces(false);
    }
  };

  const session = dashboard?.session;
  const slots = batchView?.slots || Array(12).fill(null);
  const filledCount = batchView?.filledCount ?? slots.filter(Boolean).length;
  const batchNumber = batchView?.displayBatchNumber ?? dashboard?.currentBatch?.batchNumber ?? 0;
  const totalPendingBatches = batchView?.totalPendingBatches ?? 0;
  const isCurrentBatch = batchView?.isCurrentBatch !== false;

  return (
    <div
      dir="rtl"
      className="min-h-screen bg-gradient-to-br from-slate-100 via-indigo-50/40 to-slate-100 p-4 md:p-6"
    >
      <ToastContainer position="top-center" rtl />

      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate("/production/add-orders")}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold shadow-md transition"
            >
              <Plus size={18} />
              إضافة طلبات للطابور
            </button>
            <button
              onClick={handleStartSession}
              disabled={actionLoading}
              className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl font-medium hover:bg-slate-50 transition disabled:opacity-50"
            >
              جلسة جديدة
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-2">
                <Printer className="text-indigo-600" />
                لوحة الإنتاج — طابور الطباعة
              </h1>
              <p className="text-slate-500 text-sm mt-0.5">
                تخطيط الماكينة ٦ × ٢ — ١٢ جراب لكل دفعة
              </p>
            </div>
            <button
              onClick={() => navigate("/dashboard")}
              className="p-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 transition"
            >
              <ChevronLeft size={26} />
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 rounded-2xl bg-white/60 animate-pulse" />
            ))}
          </div>
        ) : !session ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center shadow-sm">
            <Printer size={48} className="mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-bold text-slate-700 mb-2">لا توجد جلسة طباعة نشطة</h2>
            <p className="text-slate-500 mb-6">
              ابدأ جلسة جديدة أو أضف طلبات إلى طابور الطباعة
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={handleStartSession}
                className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition"
              >
                بدء جلسة
              </button>
              <button
                onClick={() => navigate("/production/add-orders")}
                className="px-6 py-3 bg-white border border-slate-200 rounded-xl font-semibold hover:bg-slate-50 transition"
              >
                إضافة طلبات
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Summary */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard
                label="الجلسة الحالية"
                value={`#${session.printSessionId}`}
                icon={Layers}
                accent="text-indigo-600"
              />
              <StatCard
                label="متبقي للطباعة"
                value={session.remainingCount}
                icon={Clock}
                accent="text-amber-600"
              />
              <StatCard
                label="مطبوع"
                value={session.printedCount}
                icon={CheckCircle2}
                accent="text-emerald-600"
              />
              <StatCard
                label="دفعات اليوم"
                value={session.batchesSinceReset ?? 0}
                icon={Package}
                accent="text-slate-700"
              />
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200/80 p-4 shadow-sm flex flex-col justify-between">
                <p className="text-sm text-slate-500 mb-2">عداد الدفعات</p>
                <button
                  type="button"
                  onClick={handleResetBatchCounter}
                  disabled={actionLoading}
                  className="flex items-center justify-center gap-2 px-3 py-2.5 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 rounded-xl text-sm font-semibold transition disabled:opacity-50"
                >
                  <RotateCcw size={16} />
                  تصفير للـ 0
                </button>
              </div>
            </div>

            {/* Current Batch */}
            <div className="bg-white/90 backdrop-blur rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-slate-800">
                      {isCurrentBatch ? "الدفعة الحالية" : "معاينة دفعة"}{" "}
                      {batchNumber >= 0 ? `#${batchNumber}` : ""}
                    </h2>
                    <p className="text-sm text-slate-500">
                      {filledCount} / 12 موضع — {session.queuedCount} في الانتظار
                      {totalPendingBatches > 0 && (
                        <span className="mr-2 text-indigo-600 font-medium">
                          · دفعة {batchOffset + 1} من {totalPendingBatches}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => runAction(refreshPrintBatch, "تم تحديث الطابور")}
                      disabled={actionLoading || isPrinting || isPrintingAll || isPrintingOrderPieces}
                      className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium transition disabled:opacity-50"
                    >
                      <RefreshCw size={16} className={actionLoading ? "animate-spin" : ""} />
                      تحديث
                    </button>
                    <button
                      onClick={handlePrintBatch}
                      disabled={actionLoading || isPrinting || isPrintingAll || isPrintingOrderPieces || filledCount === 0}
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold transition disabled:opacity-50"
                      title="طباعة ملصقات الدفعة المعروضة"
                    >
                      <Tag size={16} className={isPrinting ? "animate-pulse" : ""} />
                      {isPrinting ? "جاري الطباعة..." : "طباعة الدفعة"}
                    </button>
                    <button
                      onClick={handlePrintAllLabels}
                      disabled={actionLoading || isPrinting || isPrintingAll || isPrintingOrderPieces || (session?.remainingCount ?? 0) === 0}
                      className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-sm font-semibold transition disabled:opacity-50"
                      title="طباعة كل الملصقات المتبقية مع رقم الدفعة"
                    >
                      <Printer size={16} className={isPrintingAll ? "animate-pulse" : ""} />
                      {isPrintingAll ? "جاري طباعة الكل..." : "طباعة كل الملصقات"}
                    </button>
                    <button
                      onClick={handlePrintOrderPieceLabels}
                      disabled={
                        actionLoading ||
                        isPrinting ||
                        isPrintingAll ||
                        isPrintingOrderPieces ||
                        ((session?.remainingCount ?? 0) === 0 && filledCount === 0)
                      }
                      className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-semibold transition disabled:opacity-50"
                      title="طباعة رقم الأوردر وعدد القطع على ورق 50×25"
                    >
                      <Hash size={16} className={isPrintingOrderPieces ? "animate-pulse" : ""} />
                      {isPrintingOrderPieces ? "جاري الطباعة..." : "طباعة أرقام الأوردرات"}
                    </button>
                    <button
                      onClick={handleCompleteBatch}
                      disabled={actionLoading || isPrinting || isPrintingAll || isPrintingOrderPieces || filledCount === 0 || !isCurrentBatch}
                      className="flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-semibold transition disabled:opacity-50"
                    >
                      <CheckCircle2 size={16} />
                      إكمال الدفعة
                    </button>
                  </div>
                </div>

                {/* Batch navigation */}
                <div className="flex items-center justify-center gap-3">
                  <button
                    type="button"
                    disabled={actionLoading || !batchView?.hasPrevious}
                    onClick={() => handleNavigateBatch(batchOffset - 1)}
                    className="flex items-center gap-1 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
                    title="الدفعة السابقة"
                  >
                    <ChevronRight size={20} />
                    السابقة
                  </button>
                  <div className="px-4 py-2 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-800 text-sm font-bold min-w-[140px] text-center">
                    {totalPendingBatches > 0
                      ? `${batchOffset + 1} / ${totalPendingBatches}`
                      : "—"}
                  </div>
                  <button
                    type="button"
                    disabled={actionLoading || !batchView?.hasNext}
                    onClick={() => handleNavigateBatch(batchOffset + 1)}
                    className="flex items-center gap-1 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
                    title="الدفعة التالية"
                  >
                    التالية
                    <ChevronLeft size={20} />
                  </button>
                </div>

                {swapSourceId && (
                  <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm">
                    <span className="flex items-center gap-2 font-medium">
                      <ArrowLeftRight size={16} />
                      اختر جراب تاني للتبديل (أو تنقّل لدفعات تانية). Esc للإلغاء
                    </span>
                    <button
                      type="button"
                      onClick={() => setSwapSourceId(null)}
                      className="p-1 rounded-lg hover:bg-amber-100 transition"
                      title="إلغاء"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>

              <div className="p-4 md:p-6">
                <div
                  className="grid gap-3 md:gap-4"
                  style={{
                    gridTemplateColumns: `repeat(${MACHINE_COLUMNS}, minmax(0, 1fr))`,
                  }}
                >
                  {Array.from({ length: MACHINE_COLUMNS * MACHINE_ROWS }).map((_, index) => (
                    <PrintItemCard
                      key={slots[index]?.printQueueItemId || `empty-${index}`}
                      item={slots[index]}
                      onRemove={handleRemoveItem}
                      isLoading={actionLoading}
                      canRemove={isCurrentBatch}
                      swapSourceId={swapSourceId}
                      onSwapClick={handleSwapClick}
                    />
                  ))}
                </div>

                <div className="mt-4 flex justify-center gap-1 text-[10px] text-slate-400 font-mono">
                  <span>صف ١: ١–٦</span>
                  <span>|</span>
                  <span>صف ٢: ٧–١٢</span>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Upcoming preview */}
              <div className="bg-white/80 rounded-2xl border border-slate-200 p-5 shadow-sm">
                <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Clock size={18} className="text-amber-500" />
                  معاينة الطابور التالي
                </h3>
                {dashboard.upcomingPreview?.length > 0 ? (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {dashboard.upcomingPreview.map((item, i) => (
                      <PreviewItem key={item.printQueueItemId} item={item} index={i} />
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm text-center py-8">لا توجد عناصر في الانتظار</p>
                )}
              </div>

              {/* Printed history */}
              <div className="bg-white/80 rounded-2xl border border-slate-200 p-5 shadow-sm">
                <div className="flex items-center justify-between gap-2 mb-4">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2">
                    <CheckCircle2 size={18} className="text-emerald-500" />
                    القطع المطبوعة
                  </h3>
                  <span className="text-xs text-slate-500">{printedTotal} قطعة</span>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  اضغط «إعادة طباعة» لإدخال القطعة في الدفعة الحالية (آخر جراب يرجع للطابور التالي)
                </p>

                {isLoadingPrinted ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-14 bg-slate-100 rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : printedItems.length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-8">لا توجد قطع مطبوعة بعد</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {printedItems.map((item) => (
                      <div
                        key={item.printQueueItemId}
                        className="flex items-center gap-3 p-2 rounded-lg bg-emerald-50/60 border border-emerald-100"
                      >
                        <div className="w-12 h-12 rounded-lg bg-white overflow-hidden shrink-0 border border-emerald-100">
                          {item.designImageUrl ? (
                            <img
                              src={`${API_BASE_URL_V2}${item.designImageUrl}`}
                              alt=""
                              className="w-full h-full object-contain"
                            />
                          ) : null}
                        </div>
                        <div className="flex-1 min-w-0 text-right">
                          <p className="text-sm font-semibold text-slate-800 truncate">
                            {item.itemLabel}
                          </p>
                          <p className="text-xs text-indigo-600 truncate">
                            {item.designCode || "—"} · {item.typeName || "—"}
                          </p>
                          <p className="text-[11px] text-slate-500">
                            {item.itemIndexInOrder}/{item.totalItemsInOrder}
                            {item.machinePosition ? ` · كان #${item.machinePosition}` : ""}
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={actionLoading || reprintingId === item.printQueueItemId}
                          onClick={() => handleReprintItem(item.printQueueItemId)}
                          className="shrink-0 flex items-center gap-1 px-2.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg transition disabled:opacity-50"
                          title="إعادة طباعة"
                        >
                          {reprintingId === item.printQueueItemId ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <RotateCcw size={14} />
                          )}
                          إعادة
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {printedHasMore && (
                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      disabled={isLoadingMorePrinted}
                      onClick={() => loadPrintedItems(printedPage + 1, true)}
                      className="px-5 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-xl transition disabled:opacity-50"
                    >
                      {isLoadingMorePrinted ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 size={14} className="animate-spin" /> جاري التحميل...
                        </span>
                      ) : (
                        "تحميل المزيد"
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleCloseSession}
                disabled={actionLoading}
                className="text-sm text-slate-500 hover:text-red-600 transition disabled:opacity-50"
              >
                إغلاق الجلسة
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductionDashboardPage;
